import type { AuthProvider } from "@refinedev/core";
import { API_URL, TOKEN_KEY, USER_KEY } from "@/lib/constants";
import type { Role } from "@/lib/types";
import { MOCK_ENABLED } from "@/mock/flag";
import { mockAuthProvider } from "@/mock/mockAuthProvider";

/**
 * authProvider do melapp-admin.
 *
 * - login: POST {API_URL}/auth/login → recebe access token + user; SÓ permite
 *   entrar se `user.role === 'admin'` (a API protege /admin/* com RolesGuard,
 *   mas barramos no cliente para UX e para não guardar token de não-admin).
 * - tokens: o admin guarda o ACCESS token em localStorage (diferente do web,
 *   que mantém access em memória + refresh httpOnly). O refresh fica em cookie
 *   httpOnly setado pela API; a renovação é feita via /auth/refresh.
 *
 * TODO(Fase 2): implementar rotação de refresh transparente (interceptar 401,
 *   chamar /auth/refresh, repetir request). Hoje o 401 cai em logout.
 * TODO: OAuth Google/Apple no admin (provavelmente desnecessário — admin é
 *   conta interna com senha; manter só login por senha).
 */

interface LoginResponse {
  accessToken: string;
  user: {
    id: string;
    email: string;
    role: Role;
    nome?: string | null;
  };
}

const realAuthProvider: AuthProvider = {
  login: async ({ email, password }) => {
    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // recebe o refresh em cookie httpOnly
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        return {
          success: false,
          error: {
            name: "LoginError",
            message: "E-mail ou senha inválidos.",
          },
        };
      }

      const data = (await res.json()) as LoginResponse;

      // Guarda restrita: só admins entram no painel.
      if (data.user.role !== "admin") {
        return {
          success: false,
          error: {
            name: "ForbiddenError",
            message:
              "Acesso restrito: esta conta não tem permissão de administrador.",
          },
        };
      }

      localStorage.setItem(TOKEN_KEY, data.accessToken);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));

      return { success: true, redirectTo: "/" };
    } catch {
      return {
        success: false,
        error: {
          name: "NetworkError",
          message: "Não foi possível contatar a API. Verifique VITE_API_URL.",
        },
      };
    }
  },

  logout: async () => {
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // ignora — limpamos o estado local de qualquer forma
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    return { success: true, redirectTo: "/login" };
  },

  check: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const user = getStoredUser();
    if (token && user?.role === "admin") {
      return { authenticated: true };
    }
    return {
      authenticated: false,
      redirectTo: "/login",
      logout: true,
    };
  },

  // Erros 401/403 da API derrubam a sessão (sem refresh automático ainda).
  onError: async (error) => {
    const status = (error as { statusCode?: number })?.statusCode;
    if (status === 401 || status === 403) {
      return { logout: true, redirectTo: "/login", error };
    }
    return {};
  },

  getPermissions: async () => {
    const user = getStoredUser();
    return user?.role ?? null;
  },

  getIdentity: async () => {
    const user = getStoredUser();
    if (!user) return null;
    return {
      id: user.id,
      name: user.nome ?? user.email,
      email: user.email,
      avatar: "/mel-icon.svg",
    };
  },
};

/**
 * Em MOCK MODE (ligado por padrão) usamos o mockAuthProvider — admin fake JÁ
 * logado, sem tela de login e sem API. Defina VITE_MOCK=false para o login real.
 */
export const authProvider: AuthProvider = MOCK_ENABLED
  ? mockAuthProvider
  : realAuthProvider;

function getStoredUser(): LoginResponse["user"] | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as LoginResponse["user"];
  } catch {
    return null;
  }
}
